import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate, Link } from 'react-router'

import logo from '@/assets/images/logo.png'
import { Alert, AlertDescription, AlertTitle } from '@/components/elements/alert'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { PasswordInput } from '@/components/PasswordInput'
import { login, resendInvitation } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const Login = () => {
  // Context
  const location = useLocation()
  const navigate = useNavigate()
  const { setUser } = useContext(UserStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [passwordNotSet, setPasswordNotSet] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)

  const handleError = (message) => toast(message, 'error')
  const handleSuccess = (message) => toast(message, 'success')

  useEffect(() => {
    if (!location.pathname.includes('login')) navigate('/login', { replace: true })
  }, [location, navigate])

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  })

  // Clear the "password not set" prompt when the email field changes
  const watchedEmail = form.watch('email')
  useEffect(() => {
    setPasswordNotSet(false)
  }, [watchedEmail])

  /**
   * Handles form submission
   * - Redirects to verification page if successful
   * @param {object} data
   */
  const onSubmit = async (data) => {
    setPasswordNotSet(false)
    const loginData = await login(data, handleError, setLoading)

    if (loginData?.code === 'password_not_set') {
      setPasswordNotSet(true)
      return
    }

    if (loginData) {
      setUser({ email: data.email, ...loginData })
      navigate('/verification', {
        state: { password: data.password },
      })
    }
  }

  const onResendInvitation = async () => {
    const isValid = await form.trigger('email')
    if (!isValid) return

    const email = form.getValues('email')
    await resendInvitation({ email }, handleError, setResendLoading, handleSuccess)
  }

  /**
   * Check if the user has been redirected to this page with a URL parameter
   *
   * - `confirmed` indicates that a user has been sent to this page from `ConfirmAccount`
   * - `reset` indicates that a user has been sent to this page from `ResetPassword`
   * - `created` indicates that a user has been sent to this page from an email confirmation link
   */
  useEffect(() => {
    if (location && location.search === '?confirmed=true')
      handleSuccess('Your email address has been confirmed. You may now log in.')
    else if (location && location.search === '?reset=true')
      handleSuccess('Your password has been changed. You may now log in.')
    else if (location && location.search === '?created=true')
      handleSuccess('Your account has been created. You may now log in.')
  }, [location])

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-3xl">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <Form {...form}>
                <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col items-center text-center">
                      <h1>Welcome back</h1>
                      <p className="text-muted-foreground text-sm text-balance">
                        Login to your Xverse account
                      </p>
                    </div>
                    <FormField
                      control={form.control}
                      disabled={loading}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input data-testid="email" id="email" type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                      rules={{ required: true }}
                    />
                    <FormField
                      control={form.control}
                      disabled={loading}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center">
                            <FormLabel>Password</FormLabel>
                            <Link
                              to="/password-reset-request"
                              className="ml-auto text-sm underline-offset-2 hover:underline"
                            >
                              Forgot your password?
                            </Link>
                          </div>
                          <FormControl>
                            <PasswordInput data-testid="password" id="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                      rules={{ required: true }}
                    />
                    {passwordNotSet && (
                      <Alert>
                        <AlertTitle>Account not set up yet</AlertTitle>
                        <AlertDescription>
                          <p>
                            Check your email for your invitation link to set up your password, or
                            request a new one.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            loading={resendLoading}
                            className="mt-3"
                            onClick={onResendInvitation}
                          >
                            Resend Setup Link
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button loading={loading} type="submit" className="w-full">
                      Login
                    </Button>
                  </div>
                </form>
              </Form>

              <div className="bg-primary relative hidden items-center md:block">
                <img
                  src={logo}
                  alt="RescueX"
                  className="absolute inset-0 my-auto w-full object-cover px-6 dark:brightness-[0.2] dark:grayscale"
                />
              </div>
            </CardContent>
          </Card>
          <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
            By clicking continue, you agree to our <Link to="/terms-of-use">Terms of Use</Link> and{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
